import { message } from "antd";
import type { Category, ContentTopic } from "api/config/chalice-types";
import { useCategories } from "hooks/useCategoryCRUD";
import { Dispatch, SetStateAction, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { currentBusinessGetter } from "store/user/userSlice";
import {
  useCategoryActions,
  UseCategoryActionsReturn,
} from "./useCategoryActions";
import {
  CommonActionProps,
  useTopicActions,
  UseTopicActionsReturn,
} from "./useTopicActions";
import { CategoryModalData, useTopicsPageModals } from "./useTopicsPageModals";

// --- Main Hook: useTopicsPageActions --- //

// Props for the main coordinating hook
interface UseTopicsPageActionsPropsMain {
  displayTopics: ContentTopic[];
  setDisplayTopics: Dispatch<SetStateAction<ContentTopic[]>>;
  serverTopics: ContentTopic[];
  categories: Category[];
  categoryName: string;
  currentCategoryModalData: CategoryModalData | null;
  closeCategoryModal: () => void;
  closeModal: () => void;
  // Dependencies needed for moved action handlers
  isBulkEditMode: boolean;
  generateTopicKey: (topic: ContentTopic) => string;
  // Use ReturnType<typeof useTopicsPageModals> to get the correct types for modal openers
  openEditTopicModal: ReturnType<
    typeof useTopicsPageModals
  >["openEditTopicModal"];
  openGeneratePostsModal: ReturnType<
    typeof useTopicsPageModals
  >["openGeneratePostsModal"];
  openCategoryDeleteConfirmModal: ReturnType<
    typeof useTopicsPageModals
  >["openCategoryDeleteConfirmModal"];
  openEditCategoryModal: ReturnType<
    typeof useTopicsPageModals
  >["openEditCategoryModal"];
}

// Combined return type from sub-hooks AND new handlers
type UseTopicsPageActionsReturnMain = UseTopicActionsReturn &
  UseCategoryActionsReturn & {
    handleTopicListItemAction: (key: string, topic: ContentTopic) => void;
    handleCategorySectionAction: (
      action: string,
      categoryName: string,
      color?: string | null
    ) => void;
  };

export const useTopicsPageActions = ({
  displayTopics,
  setDisplayTopics,
  serverTopics,
  categories,
  categoryName,
  currentCategoryModalData,
  closeCategoryModal,
  closeModal,
  // Destructure new props
  isBulkEditMode,
  generateTopicKey,
  openEditTopicModal,
  openGeneratePostsModal,
  openCategoryDeleteConfirmModal,
  openEditCategoryModal,
}: UseTopicsPageActionsPropsMain): UseTopicsPageActionsReturnMain => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentBusiness = useAppSelector(currentBusinessGetter);
  const { createCategory, updateCategory, deleteCategory } = useCategories();

  const minimumTopicsMessage = useCallback(
    () =>
      message.info({
        key: "minimum-enabled-topics",
        content: "At least one topic must be enabled.",
      }),
    []
  );

  // Define common props needed by sub-hooks
  // Cast currentBusiness to any as a temporary fix for type mismatch
  const commonProps: CommonActionProps = {
    displayTopics,
    setDisplayTopics,
    currentBusiness: currentBusiness as any, // Temporary type fix
    dispatch,
    minimumTopicsMessage,
  };

  const topicActions = useTopicActions({
    ...commonProps,
    closeModal,
    navigate,
  });

  const categoryActions = useCategoryActions({
    ...commonProps,
    serverTopics, // Pass serverTopics specifically to category actions
    categories,
    categoryName,
    currentCategoryModalData,
    closeCategoryModal,
    createCategory,
    updateCategory,
    deleteCategory,
  });

  // --- Moved Action Handlers ---

  const handleTopicListItemAction = useCallback(
    (key: string, topic: ContentTopic) => {
      if (isBulkEditMode) return;

      const topicKey = generateTopicKey(topic);
      const topicIndex = displayTopics.findIndex(
        (t) => generateTopicKey(t) === topicKey
      );

      if (topicIndex === -1) {
        console.error("Failed to find topic for action:", key, topic);
        message.error("Could not find the topic to modify");
        return;
      }

      // Ensure topic has index for modal/delete actions that need it
      const topicWithIndex = {
        ...displayTopics[topicIndex],
        index: topicIndex,
      };

      try {
        if (key === "delete") {
          // Use handleDeleteTopic from topicActions
          topicActions.handleDeleteTopic(topicWithIndex, topicIndex);
        } else if (key === "toggle-enabled") {
          // Use handleToggleEnabled from topicActions
          topicActions.handleToggleEnabled(topicIndex);
        } else if (key === "edit-topic") {
          openEditTopicModal(topicWithIndex);
        } else if (key === "generate-posts") {
          openGeneratePostsModal(topicWithIndex);
        }
      } catch (error) {
        console.error(`Error performing topic action "${key}":`, error);
        message.error("Something went wrong performing the topic action");
      }
    },
    [
      isBulkEditMode,
      displayTopics,
      generateTopicKey,
      topicActions,
      openEditTopicModal,
      openGeneratePostsModal,
    ]
  );

  const handleCategorySectionAction = useCallback(
    (action: string, categoryName: string, color?: string | null) => {
      if (isBulkEditMode) {
        message.warning("Please exit bulk edit mode to manage categories");
        return;
      }

      const category = categories.find(
        (c: Category) => c.name === categoryName
      );
      const targetCategoryId =
        categoryName === "Uncategorized" ? null : category?.id;

      // Handle case where action is 'add-topic' for 'Uncategorized' - no category object exists
      if (
        !category &&
        categoryName !== "Uncategorized" &&
        action !== "add-topic" // Allow add-topic even if category is null (for Uncategorized)
      ) {
        console.error("Category not found for action:", action, categoryName);
        message.error("Could not find category details");
        return;
      }

      try {
        if (action === "delete" && category?.id) {
          const categoryToDelete = category;
          const topicsInThisCategory = displayTopics.filter(
            (t: ContentTopic) => t.category_id === categoryToDelete.id
          );

          if (topicsInThisCategory.length === 0) {
            // Use handleDeleteCategory from categoryActions
            categoryActions.handleDeleteCategory(categoryToDelete);
          } else {
            openCategoryDeleteConfirmModal(
              categoryToDelete,
              topicsInThisCategory.length
            );
          }
        } else if (action === "edit" && category?.id) {
          openEditCategoryModal(category, color);
        } else if (action === "enable-all" || action === "disable-all") {
          // Use handleToggleCategoryEnabled from categoryActions
          categoryActions.handleToggleCategoryEnabled(
            targetCategoryId,
            action === "enable-all",
            categoryName
          );
        }
        // Note: 'add-topic' case is handled directly in CategorySection component via onAddTopic prop
      } catch (error) {
        console.error(`Error performing category action "${action}":`, error);
        message.error("Something went wrong performing the category action");
      }
    },
    [
      isBulkEditMode,
      categories,
      displayTopics,
      categoryActions,
      openCategoryDeleteConfirmModal,
      openEditCategoryModal,
    ]
  );

  return {
    ...topicActions,
    ...categoryActions,
    // Return moved handlers
    handleTopicListItemAction,
    handleCategorySectionAction,
  };
};
